import { useState } from "react";
import { toast } from "sonner";
import { Check, X, Pencil, Users, FileText, LogIn, ClipboardCheck, Bell, Loader2, Images, BadgeCheck, UserCheck, Megaphone, ClipboardList, UserRound, Clock, UserPlus, KeyRound, CalendarClock, CalendarX, Trash2, RotateCcw, AlertTriangle, Wrench, Repeat, Siren, OctagonX } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import secureStorage from "../../utils/secureStorage";
import { aiApi } from "./aiApi";
import { setMickeyMood, celebrateMickey } from "./mickeyMood";
import AIMarkdown from "./AIMarkdown";

const META = {
  create_task: { label: "Cipta Tugasan", icon: ClipboardCheck, color: "text-violet-600 bg-violet-100" },
  send_reminder: { label: "Hantar Peringatan", icon: Bell, color: "text-amber-600 bg-amber-100" },
  generate_pdf: { label: "Jana PDF", icon: FileText, color: "text-rose-600 bg-rose-100" },
  generate_album_pdf: { label: "Album PDF Kenangan", icon: Images, color: "text-pink-600 bg-pink-100" },
  impersonate_user: { label: "Log Masuk Sebagai", icon: LogIn, color: "text-sky-600 bg-sky-100" },
  complete_task: { label: "Tanda Tugasan Selesai", icon: Check, color: "text-emerald-600 bg-emerald-100" },
  approve_registration: { label: "Kelulusan Pendaftaran Kanak-kanak", icon: BadgeCheck, color: "text-emerald-600 bg-emerald-100" },
  approve_user: { label: "Kelulusan Akaun Pengguna", icon: UserCheck, color: "text-teal-600 bg-teal-100" },
  broadcast_announcement: { label: "Hebahan Pukal Ibu Bapa", icon: Megaphone, color: "text-fuchsia-600 bg-fuchsia-100" },
  send_survey_reminders: { label: "Peringatan Pengesahan Sesi", icon: ClipboardList, color: "text-indigo-600 bg-indigo-100" },
  create_user: { label: "Cipta Akaun Pengguna", icon: UserPlus, color: "text-emerald-600 bg-emerald-100" },
  reset_password: { label: "Tukar Kata Laluan", icon: KeyRound, color: "text-orange-600 bg-orange-100" },
  schedule_task: { label: "Tugasan Berjadual Mimi", icon: CalendarClock, color: "text-sky-600 bg-sky-100" },
  cancel_scheduled_task: { label: "Batal Tugasan Berjadual", icon: CalendarX, color: "text-rose-600 bg-rose-100" },
  send_file: { label: "Hantar Fail Sedia Ada", icon: FileText, color: "text-emerald-600 bg-emerald-100" },
  delete_data: { label: "Padam Data (Superadmin)", icon: Trash2, color: "text-red-600 bg-red-100" },
  restore_deleted: { label: "Pulihkan Data Dipadam", icon: RotateCcw, color: "text-emerald-600 bg-emerald-100" },
  rerun_job: { label: "Jalankan Semula Tugasan", icon: Repeat, color: "text-sky-600 bg-sky-100" },
  fix_user_account: { label: "Baiki Akaun Pengguna", icon: Wrench, color: "text-amber-600 bg-amber-100" },
  restore_soft_deleted: { label: "Pulihkan Data Dipadam", icon: RotateCcw, color: "text-emerald-600 bg-emerald-100" },
  announce_maintenance: { label: "Pengumuman Gangguan Sistem", icon: Siren, color: "text-orange-600 bg-orange-100" },
  end_maintenance: { label: "Tamatkan Pengumuman Gangguan", icon: OctagonX, color: "text-rose-600 bg-rose-100" },
  skill_action: { label: "Tindakan Skill Pengurusan", icon: BadgeCheck, color: "text-pink-600 bg-pink-100" },
  skill_tool_run: { label: "Jalankan Skill (perlu pengesahan)", icon: BadgeCheck, color: "text-emerald-600 bg-emerald-100" },
};
const FIELDS = {
  generate_album_pdf: ["start_date", "end_date"], approve_registration: ["reason"], approve_user: ["branch"], send_survey_reminders: [],
  create_user: ["name", "branch", "email", "phone", "password"], reset_password: ["new_password"], schedule_task: ["message", "instruction", "run_at", "repeat"], cancel_scheduled_task: [],
  delete_data: [], restore_deleted: [], rerun_job: ["date"], fix_user_account: [], restore_soft_deleted: [], end_maintenance: [], skill_action: ["note"], skill_tool_run: [], skill_tool_run: [],
  announce_maintenance: ["message"],
};
const LABELS = { title: "Tajuk", description: "Penerangan", message: "Mesej", due_date: "Tarikh akhir", start_date: "Dari tarikh", end_date: "Hingga tarikh",
  reason: "Sebab (jika ditolak)", branch: "Cawangan", name: "Nama", email: "Emel (log masuk staf)", phone: "Telefon", password: "Kata laluan",
  new_password: "Kata laluan baharu", instruction: "Arahan untuk Mimi", run_at: "Masa (YYYY-MM-DD HH:MM)", repeat: "Ulangan (sekali/harian/mingguan)" };

function Decision({ value, editing, onChange }) {
  const opts = [["approve", "LULUS", "bg-emerald-600 text-white border-emerald-600"], ["reject", "TOLAK", "bg-rose-600 text-white border-rose-600"]];
  return (
    <div className="flex gap-1" data-testid="ai-action-decision">
      {opts.filter(([k]) => editing || k === value).map(([k, l, on]) => (
        <button key={k} type="button" disabled={!editing} onClick={() => onChange(k)}
          className={`px-2.5 py-0.5 rounded-full text-[11px] font-extrabold border transition-colors ${value === k ? on : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}>{l}</button>
      ))}
    </div>
  );
}
const STATUS = { pending: "Menunggu pengesahan", done: "Selesai", failed: "Gagal", cancelled: "Dibatalkan", superseded: "Diganti cadangan baharu", executing: "Sedang dilaksana" };

function DeletePreview({ d }) {
  return (
    <div className="rounded-xl bg-red-50/70 border border-red-200 px-3 py-2 text-[12px] text-slate-700 space-y-1.5" data-testid="ai-action-delete-preview">
      <div className="flex items-start gap-1.5 font-bold text-red-700"><AlertTriangle className="h-4 w-4 shrink-0 mt-px" /> <span data-testid="ai-action-delete-summary">{d.summary}</span></div>
      <div className="max-h-36 overflow-y-auto space-y-1">
        {(d.items || []).map((it) => (
          <div key={it.id} className="rounded-lg bg-white border border-red-100 px-2 py-1" data-testid="ai-action-delete-item">
            <div className="font-semibold text-slate-800">{it.label}</div>
            {it.sub && <div className="text-[11px] text-slate-500">{it.sub}</div>}
          </div>
        ))}
        {d.count > (d.items || []).length && <div className="text-[11px] text-slate-500">+ {d.count - d.items.length} lagi</div>}
      </div>
      <div className="text-[11px] text-red-600/80">{d.warning}</div>
    </div>
  );
}

function doImpersonate(res, me) {
  const imp = res?.result?.impersonate;
  if (!imp?.token) return;
  const curToken = secureStorage.getItem("token");
  const curUser = secureStorage.getItem("user");
  if (me.role === "superadmin") {
    secureStorage.setItem("original_superadmin_token", curToken);
    secureStorage.setItem("original_superadmin_user", curUser);
  } else {
    secureStorage.setItem("original_manager_token", curToken);
    secureStorage.setItem("original_manager_user", curUser);
  }
  secureStorage.setItem("token", imp.token);
  secureStorage.setItem("user", JSON.stringify(imp.user));
  toast.success(`Masuk sebagai ${imp.user?.name}`);
  setTimeout(() => { window.location.href = "/"; }, 150);
}

export default function AIActionCard({ action, user, onDone }) {
  const [editing, setEditing] = useState(false);
  const [args, setArgs] = useState(action.args || {});
  const [excluded, setExcluded] = useState([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(action.status);
  const meta = META[action.tool] || { label: action.tool, icon: Pencil, color: "text-slate-600 bg-slate-100" };
  const Icon = meta.icon;
  const rec = action.preview?.recipients || [];
  const pending = status === "pending";

  const set = (k, v) => setArgs((a) => ({ ...a, [k]: v }));
  const confirm = async () => {
    setBusy(true);
    setMickeyMood("thinking");
    try {
      const edited = { ...args };
      if (excluded.length) edited.exclude_ids = excluded;
      const res = await aiApi.confirm(action.id, edited);
      setStatus(res.ok ? "done" : "failed");
      res.ok ? toast.success(res.message) : toast.error(res.message);
      res.ok ? celebrateMickey() : setMickeyMood("idle");
      if (action.tool === "impersonate_user" && res.ok) doImpersonate(res, user);
      onDone && onDone(res.note);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal melaksana tindakan");
      setMickeyMood("idle");
    } finally { setBusy(false); }
  };
  const cancel = async () => {
    setBusy(true);
    try { await aiApi.cancel(action.id); setStatus("cancelled"); } catch { toast.error("Gagal membatalkan"); } finally { setBusy(false); }
  };

  return (
    <div className="mt-2 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid={`ai-action-card-${action.tool}`}>
      <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-amber-50 to-white border-b border-slate-100">
        <span className={`h-7 w-7 rounded-lg grid place-items-center ${meta.color}`}><Icon className="h-4 w-4" /></span>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-extrabold text-slate-800">{meta.label}</div>
          <div className="text-[11px] text-slate-500">Semak sebelum laksana</div>
        </div>
        <Badge variant="outline" className={`text-[10px] ${pending ? "border-amber-300 text-amber-700" : status === "done" ? "border-emerald-300 text-emerald-700" : "text-slate-500"}`}>{STATUS[status] || status}</Badge>
      </div>
      <div className="p-3 space-y-2 text-[13px]">
        {(FIELDS[action.tool] || ["title", "description", "message", "due_date"]).filter((k) => k in args || (editing && (k === "title" || k === "reason" || k === "branch"))).map((k) => (
          <div key={k}>
            <div className="text-[11px] uppercase tracking-wide font-bold text-slate-400">{LABELS[k]}</div>
            {editing ? (
              k === "description" || k === "message" ? (
                <Textarea value={args[k] || ""} onChange={(e) => set(k, e.target.value)} rows={3} className="text-[13px]" data-testid={`ai-action-edit-${k}`} />
              ) : (
                <Input type={["due_date", "start_date", "end_date"].includes(k) ? "date" : "text"} value={args[k] || ""} onChange={(e) => set(k, e.target.value)} className="h-8 text-[13px]" data-testid={`ai-action-edit-${k}`} />
              )
            ) : (
              <div className="text-slate-700 whitespace-pre-wrap">{args[k] || "-"}</div>
            )}
          </div>
        ))}
        {action.tool === "generate_pdf" && (
          <div className="max-h-40 overflow-y-auto rounded-xl bg-slate-50 p-2 border border-slate-100">
            {editing ? <Textarea value={args.content || ""} onChange={(e) => set("content", e.target.value)} rows={8} className="text-[12px] font-mono" data-testid="ai-action-edit-content" />
              : <AIMarkdown>{args.content}</AIMarkdown>}
          </div>
        )}
        {action.preview?.target && (
          <div className="rounded-xl bg-sky-50 px-3 py-2 text-sky-800"><b>{action.preview.target.name}</b> • {action.preview.target.role} • {action.preview.target.branch || "-"}</div>
        )}
        {action.preview?.album && (
          <div className="rounded-xl bg-pink-50 px-3 py-2 text-pink-800" data-testid="ai-action-album-preview">
            <div><b>{action.preview.album.child_name}</b> • {action.preview.album.photo_count} gambar • {action.preview.album.start} → {action.preview.album.end}</div>
            {action.preview.album.thumbs?.length > 0 && (
              <div className="mt-2 grid grid-cols-6 gap-1">
                {action.preview.album.thumbs.map((u) => (
                  <img key={u} src={u} alt="" loading="lazy" className="h-10 w-full object-cover rounded-md ring-1 ring-pink-200" />
                ))}
              </div>
            )}
          </div>
        )}
        {action.preview?.registration && (() => {
          const r = action.preview.registration;
          return (
            <div className="rounded-xl bg-emerald-50/60 border border-emerald-100 px-3 py-2 space-y-1.5" data-testid="ai-action-registration-preview">
              <div className="flex items-center gap-2"><div className="flex-1 font-extrabold text-slate-800">{r.nama_kanak_kanak}</div>
                <Decision value={args.decision} editing={editing && pending} onChange={(v) => set("decision", v)} /></div>
              <div className="text-[12px] text-slate-600">{r.cawangan} • {r.kelas} • Pakej: {r.pakej} • Lahir: {r.tarikh_lahir}</div>
              <div className="rounded-lg bg-white border border-emerald-100 px-2 py-1.5 text-[12px]" data-testid="ai-action-registration-creator">
                <div className="flex items-center gap-1 text-[10.5px] uppercase tracking-wide font-bold text-slate-400"><UserRound className="h-3 w-3" /> Dicipta oleh</div>
                <div className="font-bold text-slate-800">{r.dicipta_oleh?.nama} <Badge variant="outline" className="ml-1 text-[10px] border-emerald-300 text-emerald-700">{r.dicipta_oleh?.peranan}</Badge></div>
                <div className="text-slate-500">{r.dicipta_oleh?.cawangan} • {r.dicipta_oleh?.emel} • {r.dicipta_oleh?.telefon}</div>
              </div>
              <div className="flex items-center gap-1 text-[11px] text-slate-500"><Clock className="h-3 w-3" /> {r.status_label} • {r.hari_menunggu ?? "-"} hari menunggu • Lengkap: {r.status_lengkap}</div>
            </div>
          );
        })()}
        {action.preview?.user && (
          <div className="rounded-xl bg-teal-50/60 border border-teal-100 px-3 py-2 space-y-1" data-testid="ai-action-user-preview">
            <div className="flex items-center gap-2"><div className="flex-1 font-extrabold text-slate-800">{action.preview.user.name} <span className="text-[11px] font-bold text-teal-700">({action.preview.user.role})</span></div>
              <Decision value={args.decision} editing={editing && pending} onChange={(v) => set("decision", v)} /></div>
            <div className="text-[12px] text-slate-600">{action.preview.user.email} • {action.preview.user.phone} • {args.branch || action.preview.user.branch}</div>
          </div>
        )}
        {action.tool === "broadcast_announcement" && action.preview?.scope && (
          <div className="rounded-xl bg-fuchsia-50/60 border border-fuchsia-100 px-3 py-2 text-[12px] text-slate-700 space-y-1" data-testid="ai-action-broadcast-preview">
            <div><b>Skop:</b> {action.preview.scope.branch} • {action.preview.scope.class_name} • {action.preview.children} kanak-kanak</div>
            <div className="flex items-center gap-1 flex-wrap"><b>Saluran:</b>
              {[["app", "Dalam app + push"], ["whatsapp", "WhatsApp"]].map(([k, l]) => {
                const on = (args.channels || []).includes(k);
                return <button key={k} type="button" disabled={!editing || !pending} data-testid={`ai-action-channel-${k}`}
                  onClick={() => set("channels", on ? (args.channels || []).filter((c) => c !== k) : [...(args.channels || []), k])}
                  className={`px-2 py-0.5 rounded-full text-[11px] border ${on ? "bg-fuchsia-600 text-white border-fuchsia-600" : "bg-white text-slate-400 border-slate-200"}`}>{l}</button>;
              })}
            </div>
            {(args.channels || []).includes("whatsapp") && <div className="text-[11px] text-slate-500">{action.preview.with_phone} ibu bapa ada nombor telefon{action.preview.wa_dry_run ? " • MOD UJIAN WhatsApp aktif (tidak dihantar sebenar)" : ""}</div>}
          </div>
        )}
        {["create_task", "send_reminder"].includes(action.tool) && action.preview?.channels && (
          <div className="rounded-xl bg-violet-50/60 border border-violet-100 px-3 py-2 text-[12px] text-slate-700 space-y-1" data-testid="ai-action-reminder-channels">
            <div className="flex items-center gap-1 flex-wrap"><b>Saluran:</b>
              {[["app", "Dalam app + push"], ["whatsapp", "WhatsApp"]].map(([k, l]) => {
                const on = (args.channels || []).includes(k);
                return <button key={k} type="button" disabled={!editing || !pending} data-testid={`ai-action-rem-channel-${k}`}
                  onClick={() => set("channels", on ? (args.channels || []).filter((c) => c !== k) : [...(args.channels || []), k])}
                  className={`px-2 py-0.5 rounded-full text-[11px] border ${on ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-400 border-slate-200"}`}>{l}</button>;
              })}
            </div>
            {(args.channels || []).includes("whatsapp") && action.preview.with_phone != null && <div className="text-[11px] text-slate-500">{action.preview.with_phone}/{action.preview.recipient_count} penerima ada nombor WhatsApp</div>}
          </div>
        )}
        {(action.preview?.new_user || action.preview?.reset) && (() => {
          const u = action.preview.new_user || action.preview.reset;
          return (
            <div className="rounded-xl bg-emerald-50/60 border border-emerald-100 px-3 py-2 text-[12px] text-slate-700 space-y-0.5" data-testid="ai-action-account-preview">
              <div><b>{u.name}</b> • {u.role} • {u.branch}</div>
              <div>Log masuk: <code className="font-mono">{u.username || "-"}</code></div>
              <div>Kata laluan: <code className="font-mono" data-testid="ai-action-account-password">{args.password || args.new_password || u.password}</code></div>
            </div>
          );
        })()}
        {action.preview?.file && (
          <div className="rounded-xl bg-emerald-50/60 border border-emerald-100 px-3 py-2 text-[12px] text-slate-700" data-testid="ai-action-file-preview">
            <b>Fail:</b> {action.preview.file.filename} ({action.preview.file.size_kb} KB)
          </div>
        )}
        {action.preview?.schedule && (
          <div className="rounded-xl bg-sky-50/60 border border-sky-100 px-3 py-2 text-[12px] text-slate-700 space-y-0.5" data-testid="ai-action-schedule-preview">
            <div><b>Bila:</b> {action.preview.schedule.bila} • <b>Ulangan:</b> {action.preview.schedule.repeat}</div>
            <div><b>Hasil ke:</b> {(action.preview.schedule.channels || []).join(" & ")}</div>
            {action.preview.schedule.penerima?.length > 0 && <div><b>Penerima:</b> {action.preview.schedule.penerima.join(", ")}</div>}
            <div className="text-slate-500 line-clamp-3">{action.preview.schedule.message || action.preview.schedule.instruction}</div>
          </div>
        )}
        {action.preview?.survey && (
          <div className="rounded-xl bg-indigo-50/60 border border-indigo-100 px-3 py-2 text-[12px] text-slate-700" data-testid="ai-action-survey-preview">
            <div><b>{action.preview.survey.belum_jawab}</b> ibu bapa belum menjawab • Saluran: <b>{action.preview.survey.channel === "whatsapp_app" ? "WhatsApp + app" : "Dalam app"}</b></div>
            <div className="text-[11px] text-slate-500 mt-1 line-clamp-2">{(action.preview.survey.sample || []).join(", ")}...</div>
          </div>
        )}
        {action.preview?.delete && <DeletePreview d={action.preview.delete} />}
        {action.preview?.restore && (
          <div className="rounded-xl bg-emerald-50/60 border border-emerald-100 px-3 py-2 text-[12px] text-slate-700 space-y-0.5" data-testid="ai-action-restore-preview">
            <div className="font-bold text-emerald-800">{action.preview.restore.summary}</div>
            <div>Contoh: <b>{action.preview.restore.contoh}</b></div>
            <div className="text-[11px] text-slate-500">Dipadam pada {String(action.preview.restore.deleted_at || "").slice(0, 16).replace("T", " ")}</div>
          </div>
        )}
        {action.preview?.task && <div className="rounded-xl bg-emerald-50 px-3 py-2 text-emerald-800">{action.preview.task.title}</div>}
        {action.preview?.lines?.length > 0 && (
          <div className="rounded-xl bg-slate-50 px-3 py-2 space-y-0.5 text-slate-700" data-testid="ai-action-preview-lines">
            {action.preview.lines.map((l, i) => <div key={i}>{l}</div>)}
          </div>
        )}
        {rec.length > 0 && (
          <div>
            <div className="flex items-center gap-1 text-[11px] uppercase tracking-wide font-bold text-slate-400 mb-1"><Users className="h-3 w-3" /> Penerima ({rec.length - excluded.length}/{action.preview.recipient_count})</div>
            <div className="flex flex-wrap gap-1 max-h-28 overflow-y-auto">
              {rec.map((r) => {
                const off = excluded.includes(r.id);
                return (
                  <button key={r.id} type="button" disabled={!pending || !editing}
                    onClick={() => setExcluded((x) => (off ? x.filter((i) => i !== r.id) : [...x, r.id]))}
                    className={`px-2 py-0.5 rounded-full text-[11px] border transition-colors ${off ? "bg-slate-100 text-slate-400 line-through border-slate-200" : "bg-violet-50 text-violet-700 border-violet-200"} ${editing && pending ? "cursor-pointer hover:border-violet-400" : "cursor-default"}`}
                    data-testid="ai-action-recipient">{r.name}</button>
                );
              })}
            </div>
            {editing && <div className="text-[10px] text-slate-400 mt-1">Ketik nama untuk keluarkan/masukkan penerima</div>}
          </div>
        )}
      </div>
      {pending && (
        <div className="flex gap-2 px-3 pb-3">
          <Button size="sm" onClick={confirm} disabled={busy} className={`flex-1 ${action.tool === "delete_data" ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"} text-white rounded-xl h-9`} data-testid="ai-action-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : action.tool === "delete_data" ? <Trash2 className="h-4 w-4 mr-1" /> : <Check className="h-4 w-4 mr-1" />} {action.tool === "delete_data" ? "Sahkan Padam" : "Sahkan"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setEditing((e) => !e)} disabled={busy} className="rounded-xl h-9" data-testid="ai-action-edit">
            <Pencil className="h-4 w-4 mr-1" /> {editing ? "Siap" : "Ubah"}
          </Button>
          <Button size="sm" variant="ghost" onClick={cancel} disabled={busy} className="rounded-xl h-9 text-rose-600 hover:bg-rose-50" data-testid="ai-action-cancel">
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
